import { useFormContext } from 'react-hook-form';
import { AppointmentFormProvider } from './appointment-form-provider';
import { AppointmentStatusSummary } from './sections/appointment-status-summary';
import { PanelFooter } from './sections/panel-footer';
import { LocationSelector } from './sections/location-selector';
import { ProviderSection } from './sections/provider-section';
import { AppointmentProfileSection } from './sections/appointment-profile-section';
import { DatePickerSection } from './sections/date-picker-section';
import { TimeSlotsSection } from './sections/time-slots-section';
import { PatientSection } from './sections/patient-section';
import { CallbackContactSection } from './sections/callback-contact-section';
import { PaymentSection } from './sections/payment-section';
import { ReminderSection } from './sections/reminder-section';
import { ResidentialAddressSection } from './sections/residential-address-section';
import { PreviousAppointmentSection } from './sections/previous-appointment-section';
import { NoteSection } from './sections/note-section';
import { FlagsSection } from './sections/flags-section';
import { RecurrenceSection } from './sections/recurrence-section';
import { ProviderSelectionView } from './sections/provider-selection-view';
import { TimeDateSelectionView } from './sections/time-date-selection-view';
import { FlagsPickerView } from './sections/flags-picker-view';
import { FormsPickerView } from './sections/forms-picker-view';
import { ReminderPickerView } from './sections/reminder-picker-view';
import { ReminderEditorView } from './sections/reminder-editor-view';
import { RecurrencePickerView } from './sections/recurrence-picker-view';
import { CreateContactView } from './sections/create-contact-view';
import { EditInsuranceView } from './sections/edit-insurance-view';
import { CreatePatientView } from './sections/create-patient-view';
import { AppointmentStatusManager } from './appointment-status-manager';
import { CompletionRail } from '@/scheduling/ai/completion/completion-rail';
// Pure helper — moved out of the component file so it exports components
// only (rail session, c219d84b, fixing the react-refresh lint pair).
import { deriveCompletionResolution } from '@/scheduling/ai/completion/completion-rail-derive';
import {
  composeCompletionRail,
  bookEnabled,
  type CompletionCard,
} from '@/scheduling/ai/completion/composition-engine';
import { useRailChoreography } from '@/scheduling/ai/completion/use-rail-choreography';
import { toast } from 'sonner';
import { useBookAppointment } from '@/services/scheduling/use-book-appointment';
import { buildSavedBookingSeed } from './saved-booking-seed';
import { useUpdateAppointment } from '@/services/scheduling/use-update-appointment';
import { transformFormToRequest } from './transform-form-to-request';
import { buildEditAppointmentBody } from '@/scheduling/lib/form-edit-overlay';
import { cn } from '@/lib/utils';
import { MessageSquare, Repeat, Send, Flag, Bell, X, Pencil, Maximize2, Minimize2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/api-client';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { ProviderFilter } from '@/shared/store/create-appointment-handoff';
import type { AppointmentResponse } from '@/types/scheduling-api';
import type { HealthInsurance } from '@/services/scheduling/use-prebook-selected-patient';
import { useFormContext as useFC } from 'react-hook-form';

/** Stable empty list so the hoisted rail hook keeps a constant identity. */
const EMPTY_RAIL_CARDS: CompletionCard[] = [];

/**
 * CreateAppointmentPanel — Block (inline left panel)
 * Section order matches Flutter create_appointment_widget.dart slot order:
 *   Location → Provider → Profile → Date & Time → Patient
 *   → Previous Appointment (FOLLOWUP) → Residential Address (Home)
 *   → Callback Contact → Reminder → Payment → Note → Flags → Recurrence → Actions
 *
 * Profile drives slot duration / reasonCode / room / billing — must come
 * before Date & Time so slot lookup can filter by appointmentProfileId.
 * Rendered inline by FrameScheduling — NOT as a Sheet/drawer. Width follows
 * the `layout` prop: classic 380px / wide clamp(420px,33vw,560px) — see
 * `use-booking-rail-layout.ts` for the per-org gate.
 */

/** What a successful create save hands back to onSaveSuccess (audit #4):
 *  enough for the AI chain to advance without re-deriving what the human
 *  just confirmed — the picked patient and the booked slot's real end. */
export interface SavedBookingSeed {
  /** The id of the appointment the save created (PROD-1395: the Referrals desk links it to a referral). */
  appointmentId?: string;
  patientId?: string;
  patientName?: string;
  slotStart?: string;
  slotEnd?: string;
}

interface CreateAppointmentPanelProps {
  open: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit' | 'reschedule' | 'cancel';
  organizationId: string;
  practiceId: string;
  roomId?: string;
  userId: string;
  onSearchPatients: (query: string) => Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    mrn?: string;
    dob?: string;
    phone?: string;
  }>>;
  initialValues?: Partial<AppointmentFormValues>;
  /** Referral handoffs only: the Provider section lists one discipline (undefined everywhere else). */
  providerFilter?: ProviderFilter;
  /**
   * Seed for the Patient section's search box (Scheduling-AI command bar,
   * VITE_SCHEDULING_AI — fail-closed; undefined everywhere else). Travels
   * OUTSIDE `initialValues` because the form only carries a PICKED
   * patient (`patientId` + cached display fields); the search box text is
   * PatientSection-local state. Participates in `formKey` so a new AI
   * draft remounts the form with the new seed. The AI never auto-picks —
   * the user drives the existing search/select flow.
   */
  initialPatientQuery?: string;
  /**
   * AI-fill checklist (Scheduling-AI drafts only, undefined everywhere
   * else): which fields the prompt filled vs which need the user, rendered
   * as a strip at the top of the rail. See `ai/ai-fill-report.ts`.
   */
  aiFillReport?: import('@/scheduling/ai/ai-fill-report').AiFillItem[];
  /**
   * The active Scheduling-AI draft (flag-gated, undefined everywhere else).
   * When present the panel renders the COMPLETION RAIL — the generated
   * checklist over the locked card vocabulary — instead of the flat
   * `aiFillReport` chip strip (Option B, 06-decisions/2026-08-11). Cards
   * read/write the SAME form state the manual sections use; the strip
   * remains as the no-draft fallback only.
   */
  aiDraft?: import('@/scheduling/ai/scheduling-command-stub-parser').SchedulingCommandDraft | null;
  /**
   * Downstream rejection to surface as the rail's ConflictCard (the
   * PROD-1846 alternatives verdict, a lost slot race) — never a dead toast.
   */
  aiConflict?: string;
  /** The rail header's × — drop the AI draft, keep the manual rail. */
  onAiDismiss?: () => void;
  /**
   * Sub-view to open on mount (AI drafts with a recurrence land straight on
   * the recurrence configurator — the exact step the prompt cannot fill).
   */
  aiInitialView?: 'recurrence-config';
  /** Identity of the active AI draft — participates in formKey (remount on new draft). */
  aiDraftKey?: string;
  /**
   * Fires after a SUCCESSFUL create save, before/independent of onClose — the
   * Scheduling-AI chain uses it to advance to the next back-to-back leg.
   * Carries the saved booking's patient + slot (audit #4): the next leg
   * must start when THIS visit ends and must keep the patient the human
   * already picked, instead of re-searching them by name.
   */
  onSaveSuccess?: (saved?: SavedBookingSeed) => void;
  /**
   * Fires after a SUCCESSFUL edit/reschedule save with the appointment's NEW
   * start (local-clock wire string, may be null if the form never held one).
   *
   * The calendar owns its own selected day, and a reschedule that moves an
   * appointment to another date used to leave the calendar parked on the OLD
   * day — so the appointment simply vanished from the grid and the user could
   * not confirm the move without navigating there by hand (reported
   * 2026-08-21). The scheduling frame uses this to follow the appointment.
   */
  onEditSaveSuccess?: (newStart: string | null) => void;
  /**
   * The CALENDAR's currently-selected day as `YYYY-MM-DD`, pushed down while
   * the edit/reschedule rail is open.
   *
   * Flutter parity: the calendar's own date change writes straight onto the
   * open panel's appointment — `calendar_screen.dart:2481-2484`
   * (`ChangeDateState` → `selectedAppointment.selectedDate = selectedDate`) —
   * and the Time & Date sub-page reads exactly that field
   * (`time_date_selection_widget.dart:91`). Without this the sub-page stayed
   * pinned to the appointment's original day: the user moved the calendar to
   * Aug 31, opened Time & Date, and got Aug 27 (reported 2026-08-21).
   *
   * NOT applied on mount — the panel opens on the appointment's own day, which
   * is what Flutter's PreLoad seeds (`selectedDate = appointment.start`).
   */
  calendarDateYmd?: string;
  /**
   * Book-time slot rejection (audit #8): fired when Save fails with
   * SlotPassed/SlotBooked — the backend's atomic claim guard (PROD-1844)
   * said the slot is gone. The frame flips the rail's ConflictCard so the
   * user gets a tappable recovery instead of a checked Time row over a
   * Save that keeps failing.
   */
  onAiSlotConflict?: () => void;
  /**
   * The user committed a REPLACEMENT slot while a conflict verdict was
   * active (Codex P1 on 6be97d8a): without this the ConflictCard stays
   * open and the Book gate stays shut even after recovery.
   */
  onAiConflictResolved?: () => void;
  /**
   * The loaded appointment, REQUIRED in `mode="edit"`. Save overlays the
   * edited form fields onto this payload and PUTs it (Flutter
   * `UpdateAppointmentEvent(selectedAppointment)` — the update body is the
   * whole appointment echoed back, NOT a Book-shaped request). Ignored in
   * create mode.
   */
  editAppointment?: AppointmentResponse | null;
  /**
   * Which screen edge this panel sits on — decides where its divider
   * border draws (Codex on the r2 delta: the calendar frame moved the
   * panel RIGHT, but ViewAppointmentPanelPage and the shell overlay still
   * mount it LEFT of their content; a global border-l left those with the
   * divider on the outside edge). Default 'right' = the calendar frame.
   */
  edge?: 'left' | 'right';
  /**
   * Geometry mode (per-org gate, `use-booking-rail-layout.ts`):
   * 'classic' = fixed 380px (pre-r2 production parity, Flutter ~392);
   * 'wide'    = clamp(420px,33vw,560px) ("one-third for the rail",
   *             Samer 2026-08-12 — drawn for the Scheduling-AI rail).
   * Default 'wide' preserves current dev behaviour; FrameScheduling and
   * ViewAppointmentPanelPage pass the resolved mode explicitly.
   */
  layout?: 'classic' | 'wide';
  /**
   * Whether a SUCCESSFUL save auto-closes the panel (invokes `onClose`).
   * Default `true` — the inline create/edit/reschedule rail closes itself on
   * save (Flutter parity: the left rail dismisses once the appointment saves).
   *
   * The full-page `ViewAppointmentPanelPage` passes `false`: saving there must
   * KEEP the user on the appointment screen (they continue across the Patient/
   * Vitals/Billing/… tabs). Only the panel's Close/Cancel button navigates
   * back — `onClose` is still wired for that, it just isn't fired on save.
   */
  closeOnSaveSuccess?: boolean;
  /**
   * When `true` the panel is mounted inside a centered modal (not the inline
   * calendar rail). Drops the fixed width constraint and the side border so
   * the panel fills the modal container edge-to-edge, and upgrades the header
   * title and footer border weight to match the larger surface.
   */
  modal?: boolean;
}

/**
 * SendFormRow — selected-form card matching Flutter
 * `questionnaire_widget.dart:37–101`: send icon + form name (bold) + status
 * + edit pencil (opens picker) + delete trash (clears the selection).
 * Renders only when a form has been picked.
 */
function SendFormRow({ onEdit }: { onEdit?: () => void }) {
  const { watch, setValue } = useFC<AppointmentFormValues>();
  const id = watch('questionnaireId');
  const name = watch('questionnaireName');
  const status = watch('questionnaireStatus');
  if (!id) return null;

  const clear = () => {
    setValue('questionnaireId', null);
    setValue('questionnaireName', null);
    setValue('questionnaireStatus', null);
  };

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Form
      </p>
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
        <Send className="h-5 w-5 text-brand-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate leading-snug">
            {name ?? '—'}
          </p>
          {status ? (
            <p className="text-xs text-text-muted leading-snug">{status}</p>
          ) : null}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
            aria-label="Edit form"
          >
            <Pencil className="h-4 w-4 text-brand-primary" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={clear}
          className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
          aria-label="Clear form"
        >
          <X className="h-4 w-4 text-status-error" />
        </button>
      </div>
    </div>
  );
}

/**
 * ActionsRow — Flutter parity for the bottom 5-icon button strip
 * (`create_appointment_widget.dart:1893–2097`). Each icon shows ONLY
 * when its section is empty; tapping reveals the section above by
 * seeding its form fields. Recurrence is gated on the same prereqs
 * Flutter checks (provider + profile + billingProfileId + slot + patient).
 */
function ActionsRow({
  onOpenFlags,
  onOpenSendForm,
  onOpenReminder,
  onOpenRecurrence,
  reminderAddDisabled = false,
  hideRecurrence = false,
}: {
  onOpenFlags?: () => void;
  onOpenSendForm?: () => void;
  onOpenReminder?: () => void;
  onOpenRecurrence?: () => void;
  /**
   * Disable ONLY the Reminder add-button (Flutter `reminderButtonWidget`
   * `:1947`): unlike Note / Flag / Send-Form (Booked/Proposed/Pending), the
   * reminder add-button is gated to Booked/Proposed — a Pending appointment
   * may NOT add a reminder. The other buttons stay enabled.
   */
  reminderAddDisabled?: boolean;
  /**
   * Hide the Recurrence add-button entirely. Set in RESCHEDULE mode: you
   * can't add/change a recurrence pattern while moving an occurrence, and
   * Flutter drops the recurrence button whenever the appointment already
   * exists (`create_appointment_widget.dart:1591` gates it on
   * `isBlank(appointment.id)`).
   */
  hideRecurrence?: boolean;
}) {
  const { watch, setValue } = useFC<AppointmentFormValues>();
  const comment = watch('comment');
  const recurring = watch('recurring');
  const reminderProfileId = watch('reminderProfileId');
  const questionnaireId = watch('questionnaireId');
  const patientFlags = watch('patientFlags');
  const flagIds = watch('flagIds');

  const providerId = watch('providerId');
  const profileId = watch('appointmentProfileId');
  const billingProfileId = watch('billingProfileId');
  const slotStart = watch('slotStart');
  const slotEnd = watch('slotEnd');
  const patientId = watch('patientId');
  void slotStart;
  void slotEnd;
  void setValue;

  const recurrenceEnabled =
    !!providerId && !!profileId && !!billingProfileId && !!slotStart && !!slotEnd && !!patientId;

  const showNote = comment !== null;
  const showReminder = !!reminderProfileId;
  const showSendForm = !!questionnaireId;
  const showFlag = (patientFlags?.length ?? 0) > 0 || (flagIds?.length ?? 0) > 0;
  const showRecurrence = !!recurring;

  // All sections set → no icons left to render → hide the row entirely.
  if (showNote && showReminder && showSendForm && showFlag && showRecurrence) {
    return null;
  }

  const buttons: Array<{
    key: string;
    icon: typeof MessageSquare;
    label: string;
    visible: boolean;
    enabled?: boolean;
    onClick: () => void;
  }> = [
    {
      key: 'note',
      icon: MessageSquare,
      label: 'Note',
      visible: !showNote,
      onClick: () => setValue('comment', ''),
    },
    {
      key: 'reminder',
      icon: Bell,
      label: 'Reminder',
      visible: !showReminder,
      // Auto-populated by PreBookSelectedAppointmentProfile on profile
      // pick; this button opens the manual override picker (Flutter
      // `reminder_profile_selection_widget.dart`). Gated to Booked/Proposed
      // in edit (no Pending) — see `reminderAddDisabled`.
      enabled: !!onOpenReminder && !reminderAddDisabled,
      onClick: () => onOpenReminder?.(),
    },
    {
      key: 'send-form',
      icon: Send,
      label: 'Send Form',
      visible: !showSendForm,
      // Opens the full-screen Forms picker (Flutter
      // `forms_selection_widget.dart`). The picker scopes by provider,
      // so it's only enabled once a provider is selected.
      enabled: !!providerId && !!onOpenSendForm,
      onClick: () => onOpenSendForm?.(),
    },
    {
      key: 'flag',
      icon: Flag,
      label: 'Flag',
      visible: !showFlag,
      // Opens the full-screen Flags picker (Flutter
      // `flags_selection_widget.dart`). Disabled until a patient is
      // picked — the picker pre-checks based on `patientFlags`, which
      // is seeded by PreBookSelectedPatient on patient pick.
      enabled: !!patientId && !!onOpenFlags,
      onClick: () => onOpenFlags?.(),
    },
    {
      key: 'recurrence',
      icon: Repeat,
      label: 'Recurrence',
      visible: !showRecurrence && !hideRecurrence,
      // Opens the full-screen Recurrence configurator (Flutter
      // `recurrence_appointment_widget.dart`). Same prereqs as Flutter:
      // provider + profile + billing + slot + patient must be set.
      enabled: recurrenceEnabled && !!onOpenRecurrence,
      onClick: () => onOpenRecurrence?.(),
    },
  ];

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <div className="flex items-center gap-2">
        {buttons
          .filter((b) => b.visible)
          .map(({ key, icon: Icon, label, onClick, enabled = true }) => (
            <button
              key={key}
              type="button"
              onClick={enabled ? onClick : undefined}
              disabled={!enabled}
              className={cn(
                'flex flex-col items-center gap-1 flex-1 py-2 rounded-lg border transition-colors',
                enabled
                  ? 'border-border-default text-text-muted hover:border-brand-primary hover:text-brand-primary'
                  : 'border-border-subtle text-text-hint cursor-not-allowed opacity-60',
              )}
              title={enabled ? label : `${label} (unavailable)`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

/** NoteSection wrapper that hides until the Note button reveals it.
 * Mirrors Flutter's `comment != null` gate (slot 10): bottom-row icon
 * sets `comment` to '' to reveal; the section's clear (×) sets it back
 * to null to re-hide. */
function NoteSlot() {
  const { watch } = useFC<AppointmentFormValues>();
  const comment = watch('comment');
  if (comment === null) return null;
  return <NoteSection />;
}

/** RecurrenceSection wrapper — only show once a recurrence has been seeded. */
function RecurrenceSlot({ onEdit }: { onEdit?: () => void }) {
  const { watch } = useFC<AppointmentFormValues>();
  const recurring = watch('recurring');
  if (!recurring) return null;
  return <RecurrenceSection onEdit={onEdit} />;
}

/** Appointment statuses under which the status-gated sections stay editable
 *  in edit mode. Verbatim from Flutter `app_constants.dart` status codes
 *  (`appointmentStatusBooked/Proposed/Pending`). */
const EDITABLE_EDIT_STATUSES = new Set(['Booked', 'Proposed', 'Pending']);

/** Statuses under which the billing-profile row inside the appointment-profile
 *  card is locked REGARDLESS of the invoice state. Flutter
 *  `appointment_profile_widget.dart:408-415`. */
const BILLING_BLOCKED_EDIT_STATUSES = new Set(['cancelled', 'noshow']);

/** Statuses under which the Reminder ADD-button stays enabled in edit mode.
 *  Flutter `reminderButtonWidget` (`:1947`) excludes Pending — narrower than
 *  the other bottom-row buttons (Note / Flag / Send-Form), which allow it. */
const REMINDER_ADD_EDIT_STATUSES = new Set(['Booked', 'Proposed']);

/**
 * SectionLock — read-only wrapper for status-gated sections in edit mode.
 * Flutter wraps locked sections in `IgnorePointer`; `pointer-events-none`
 * is the DOM equivalent. `opacity-60` signals read-only; `aria-disabled`
 * carries it to assistive tech. Passthrough when not locked.
 */
function SectionLock({
  locked,
  children,
}: {
  locked: boolean;
  children: React.ReactNode;
}) {
  if (!locked) return <>{children}</>;
  return (
    <div className="pointer-events-none opacity-60" aria-disabled>
      {children}
    </div>
  );
}

/**
 * Walk a (possibly nested) RHF errors object and return every leaf path.
 * Object.entries on `formState.errors` only sees top-level keys, so nested
 * issues like `emergencyContacts.0.relationship` are invisible — surfacing
 * them here gives the diagnostic tooltip something useful to show.
 */
function collectErrorPaths(errors: unknown, prefix = ''): string[] {
  if (!errors || typeof errors !== 'object') return [];
  const out: string[] = [];
  const e = errors as Record<string, unknown>;
  // Leaf: { type, message, ... }
  if (typeof e.message === 'string' || typeof e.type === 'string') {
    out.push(prefix || '(root)');
    return out;
  }
  for (const [key, val] of Object.entries(e)) {
    if (key === 'ref') continue;
    out.push(...collectErrorPaths(val, prefix ? `${prefix}.${key}` : key));
  }
  return out;
}

/** The booking header's icon buttons: 32 px, quiet, grey hover (the × and the full-view switch). */
const HEADER_ICON_BUTTON =
  'flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-grey-100 hover:text-text-primary';

function CreateAppointmentForm({
  mode = 'create',
  onClose,
  organizationId,
  practiceId,
  roomId = '',
  userId,
  providerFilter,
  editAppointment = null,
  closeOnSaveSuccess = true,
  initialPatientQuery,
  aiFillReport,
  aiDraft,
  aiConflict,
  onAiDismiss,
  aiInitialView,
  aiDraftKey,
  onSaveSuccess,
  onEditSaveSuccess,
  calendarDateYmd,
  onAiSlotConflict,
  onAiConflictResolved,
  modal = false,
  expanded = false,
  onToggleExpanded,
}: Omit<CreateAppointmentPanelProps, 'open' | 'initialValues' | 'onSearchPatients'> & {
  onSearchPatients?: unknown;
  /** The rail form is showing in the centred full view (CreateAppointmentPanel owns the switch). */
  expanded?: boolean;
  /** Rail ↔ full view. Absent when there is no rail to go back to (CreateAppointmentModal). */
  onToggleExpanded?: () => void;
}) {
  const form = useFormContext<AppointmentFormValues>();
  const bookAppointment = useBookAppointment();
  const updateAppointment = useUpdateAppointment();
  // "Existing appointment" surface — Flutter opens the SAME left panel for both
  // Edit and Reschedule (reschedule is a constrained edit; in the PM app both
  // save via PUT /Appointment/{id}). So the update/save path, provider & patient
  // locks, and submit spinner apply to BOTH modes.
  const isEditMode = (mode === 'edit' || mode === 'reschedule') && !!editAppointment;
  // Edit-ONLY affordances (hidden in reschedule, per Flutter's reschedule mode
  // which suppresses the status-transition buttons + Cancel block).
  const isEditOnly = mode === 'edit' && !!editAppointment;
  const isRescheduleMode = mode === 'reschedule' && !!editAppointment;
  const isSubmitting = isEditMode
    ? updateAppointment.isPending
    : bookAppointment.isPending;
  // Status gate (Flutter `showEdit`): the status-gated sections (profile /
  // date-time / patient / reminder / note / flags / forms / recurrence) are
  // editable only when the appointment is Booked / Proposed / Pending. The
  // Provider is ALWAYS locked in edit (own `locked` prop), and Payment is
  // ALWAYS editable (no status gate) — see CONVERSION_NOTES.md 3-tier model.
  const sectionsLocked =
    isEditMode &&
    !EDITABLE_EDIT_STATUSES.has(editAppointment?.status?.code ?? '');
  // Payment lock (Flutter `payment_widget.dart:359`): the sponsor switcher is
  // wrapped in `IgnorePointer(ignoring: (invoiceStatus != Draft && id != null)
  // || status == Cancelled)`. For an existing appointment (always true in edit)
  // that reduces to: editable ONLY while the invoice is still a draft and the
  // appointment isn't cancelled. Identical to the gate shipped in the View
  // popup (PROD-1010). Create mode (id == null) is never locked.
  const invoiceStatus = (editAppointment?.invoice?.invoiceStatus ?? '').toLowerCase();
  const isCancelledStatus =
    (editAppointment?.status?.code ?? '').toLowerCase() === 'cancelled';
  const paymentLocked =
    isEditMode && !(invoiceStatus === 'draft' && !isCancelledStatus);
  // ── Appointment-profile card: THREE independent gates, not one ──────────
  //
  // Flutter hands `AppointmentProfileWidget` a single `showEdit` flag but only
  // applies it to the card's pencil/delete row. The Reason-for-visit field and
  // the billing row each carry their own `IgnorePointer`. Porting the section
  // inside the group-A `SectionLock` collapsed all three into the narrowest
  // gate, with a hard consequence: an appointment whose `reasonCode` is null
  // (bookable — appointment profiles can be configured with no reasonCode;
  // 4 of 9 profiles on dev provider 0c198841 have `reasonCode: null`) fails the
  // schema's `reasonCode required` rule, so Save is disabled for EVERY field,
  // and the RFV picker that would clear the error was locked too. Net effect:
  // once such an appointment left Booked/Proposed/Pending it could not be
  // edited at all from React, while Flutter could still fix it. Reproduced on
  // dev 2026-09-09 with appointment 9f60f6b7-368d-419e-81f8-0ed49d257128
  // (status NoShow, root + nested reasonCode null, provider has 9 RFVs).
  // See CONVERSION_NOTES.md §5.
  //
  // 1. Card pencil / clear-× / alternative-profile grid → Flutter `showEdit`
  //    (`create_appointment_widget.dart:1134`) — same statuses as group A.
  const profileEditLocked = sectionsLocked;
  // 2. Reason for visit → `appointment_profile_widget.dart:365-373`: open the
  //    popup when options exist AND status != Cancelled. Status is the only
  //    lock; the empty-options half lives in the section itself.
  const reasonLocked = isEditMode && isCancelledStatus;
  // 3. Billing profile + Add-procedure-codes → `appointment_profile_widget.dart
  //    :408-415`: `(invoiceStatus != Draft && id != null) || status ∈
  //    {Cancelled, NoShow}`. In edit `id != null` always holds.
  const billingLocked =
    isEditMode &&
    (invoiceStatus !== 'draft' ||
      BILLING_BLOCKED_EDIT_STATUSES.has(
        (editAppointment?.status?.code ?? '').toLowerCase(),
      ));
  // Reminder add-button: enabled in edit only for Booked/Proposed (no Pending).
  const reminderAddLocked =
    isEditMode && !REMINDER_ADD_EDIT_STATUSES.has(editAppointment?.status?.code ?? '');
  // The status-transition menu (Mark As Arrived / No Show / Checked In /
  // Unmark* / Confirm / Unmark No Show) AND Cancel Appointment live INSIDE the
  // left edit panel (Flutter parity, create_appointment_widget.dart:2562-2707)
  // via <AppointmentStatusManager variant="panel">. Edit mode only; the manager
  // owns its own Cancel + arrived-guard dialogs and drops itself for
  // Cancelled / Fulfilled.
  const qc = useQueryClient();
  const providerId = form.watch('providerId');
  const appointmentProfileId = form.watch('appointmentProfileId');
  const patientId = form.watch('patientId');
  const [view, setView] = useState<
    | 'create'
    | 'provider-selection'
    | 'time-date-selection'
    | 'flags-selection'
    | 'forms-selection'
    | 'reminder-selection'
    | 'reminder-editor'
    | 'recurrence-config'
    | 'create-contact'
    | 'edit-insurance'
    | 'create-patient'
  >(aiInitialView ?? 'create');

  /**
   * Completion-rail rule 6 — "doctor change resets visit + time" (Codex P1
   * on the rail commit: ProviderSelectionView writes the new provider —
   * and often a new first profile — but never clears a slot picked for the
   * PREVIOUS doctor, so the rail could show zero open asks over a
   * mismatched provider/slot and Book stays enabled). When the provider
   * actually CHANGES while an AI draft is active: a profile that did NOT
   * change with it is the old doctor's → clear it; a slot that did NOT
   * change with it is the old doctor's → clear it. Values rewritten in the
   * same pick (the provider view seeds its first profile, quick-slot taps
   * set the slot) are kept. Gated to rail mode so the manual flow's
   * long-standing behaviour is untouched.
   */
  const prevProviderPickRef = useRef<{
    providerId: string | null;
    appointmentProfileId: string | null;
    slotStart: string | null;
  } | null>(null);
  const railSlotStart = form.watch('slotStart');
  useEffect(() => {
    if (!aiDraft) return;
    const prev = prevProviderPickRef.current;
    prevProviderPickRef.current = {
      providerId,
      appointmentProfileId,
      slotStart: railSlotStart,
    };
    if (!prev || prev.providerId === providerId || !prev.providerId || !providerId) {
      return;
    }
    if (prev.appointmentProfileId && prev.appointmentProfileId === appointmentProfileId) {
      form.setValue('appointmentProfileId', null, { shouldValidate: true });
      form.setValue('appointmentProfileName', null);
      form.setValue('appointmentProfileColor', null);
      form.setValue('pickedProfile', null);
    }
    if (prev.slotStart && prev.slotStart === railSlotStart) {
      form.setValue('slotId', null);
      form.setValue('slotStart', null, { shouldValidate: true });
      form.setValue('slotEnd', null);
    }
  }, [aiDraft, providerId, appointmentProfileId, railSlotStart, form]);

  /**
   * Completion-rail tap routing (rule: every answer is tappable). Kinds
   * with a dedicated sub-view open it — the SAME views the manual flow
   * uses; inline sections scroll into view instead. The rail itself never
   * writes form state here: answering happens in the section, and the
   * rail re-composes from the form.
   */
  const handleCompletionAction = (
    kind: import('@/scheduling/ai/completion/composition-engine').CompletionCardKind,
  ) => {
    switch (kind) {
      case 'practitioner':
        setView('provider-selection');
        return;
      case 'time-slot':
      case 'conflict':
        setView('time-date-selection');
        return;
      case 'recurrence-dates':
        // The recurrence configurator needs a committed initial slot to run
        // its availability checks — without one it silently no-ops (Codex
        // P1 on the rail commit; same dead end the aiInitialView guard
        // closes). No slot yet → collect it in time/date selection first;
        // the rail re-asks the dates right after.
        setView(
          form.getValues('slotStart') ? 'recurrence-config' : 'time-date-selection',
        );
        return;
      case 'register-patient':
        setView('create-patient');
        return;
      case 'patient':
      case 'visit-type':
      case 'receipt': {
        const target = kind === 'visit-type' ? 'visit-type' : kind;
        document
          .querySelector(`[data-completion-target="${target}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      default:
        return;
    }
  };

  /**
   * THE SINGLE COMPOSITION for this render — the rail's cards, and the
   * source for both the Book gate below and the hoisted choreography.
   *
   * Hoisted here (above the subview early-returns) on purpose: those
   * returns replace the entire child tree, so CompletionRail UNMOUNTS
   * whenever a picker is open. A choreography held inside the rail died
   * with it, and returning from a picker treated the live draft as brand
   * new — re-cascading every row instead of resolving the one card the
   * user just answered, on the single most common interaction in the rail.
   * Found by Codex, P1(a). CreateAppointmentForm itself never unmounts
   * across those returns, so the state survives the round-trip here. That
   * also completes P1(b): the pending rail-tap token outlives however long
   * the human spends inside the picker, which a wall-clock window never
   * could.
   *
   * Keyed on draft identity, so a genuinely NEW request still re-cascades.
   *
   * Composed ONCE and shared: an earlier state of this file composed the
   * same cards twice per render — once for the Book gate, once for the
   * choreography. Two compositions cannot disagree today, but the day one
   * gains a condition the other lacks, Book and the rail would silently
   * disagree about whether the draft is finished.
   */
  const aiRailCards = aiDraft
    ? composeCompletionRail(
        aiDraft,
        deriveCompletionResolution({
          patientId: form.watch('patientId'),
          providerId: form.watch('providerId'),
          appointmentProfileId: form.watch('appointmentProfileId'),
          slotStart: form.watch('slotStart'),
          slotEnd: form.watch('slotEnd'),
          recurringSlotCount: form.watch('recurring')?.slots?.length ?? 0,
          hasRecurrence: !!aiDraft.recurrence,
          ...(aiConflict ? { conflict: aiConflict } : {}),
        }),
      )
    : EMPTY_RAIL_CARDS;
  const aiRailChoreography = useRailChoreography(aiRailCards, aiDraftKey);

  // Rule 5 (audit #9): the composition engine's Book gate, over the SAME
  // cards the rail renders. True (open gate) with no AI draft — manual
  // bookings are Zod-only, unchanged.
  const aiBookGate = !aiDraft ? true : bookEnabled(aiRailCards);

  // Conflict recovery (Codex P1 on 6be97d8a): committing a replacement
  // slot while a verdict is active resolves it. The conflict path cleared
  // slotId, so any truthy slotId arriving while aiConflict is set means
  // the user picked again. Parent-callback-in-effect is the sanctioned
  // parent sync (the repo rule bans direct setState in effects).
  const watchedSlotId = form.watch('slotId');
  const aiConflictRef = useRef(!!aiConflict);
  useEffect(() => {
    aiConflictRef.current = !!aiConflict;
  }, [aiConflict]);
  useEffect(() => {
    if (watchedSlotId && aiConflictRef.current) onAiConflictResolved?.();
  }, [watchedSlotId, onAiConflictResolved]);

  // Calendar → panel browsing-date sync (Flutter `ChangeDateState`). Skips the
  // first run so the appointment's own day survives the open; afterwards every
  // calendar move follows into the form, which is what the Time & Date
  // sub-page and the inline slot strip both read.
  const lastCalendarDateRef = useRef<string | undefined>(calendarDateYmd);
  useEffect(() => {
    if (lastCalendarDateRef.current === calendarDateYmd) return;
    lastCalendarDateRef.current = calendarDateYmd;
    if (!calendarDateYmd) return;
    const [y, m, d] = calendarDateYmd.split('-').map(Number);
    if (!y || !m || !d) return;
    const next = new Date(y, m - 1, d);
    next.setHours(0, 0, 0, 0);
    form.setValue('selectedDate', next.toISOString(), { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDateYmd]);

  const handleSave = () => {
    // Belt to the buttons' braces (Codex P1): no save path — footer,
    // header, or any future caller — bypasses the AI gate.
    if (!aiBookGate) return;
    const values = form.getValues();

    // Sponsor save guards — Flutter `onSave` (create_appointment_widget.dart
    // :2487-2501) blocks the save (toast, no mutation) when a non-Self-pay
    // sponsor is invalid. Applies to create + edit.
    //
    // 1. No sponsor selected. The Zod schema raises this on `sponsorId`, but
    //    the Save button only reflects errors for fields that were validated,
    //    and picking Insurance validated `sponsorType` alone. So a patient with
    //    NO insurance on file could be saved as Insurance with no insurer
    //    (prod, 2026-10-01, at check-in), which then billed as insured. Guard
    //    it here, where the request is issued, as well as on the button.
    if (
      values.sponsorType &&
      values.sponsorType !== 'Self-Pay' &&
      !values.sponsorId
    ) {
      toast.error(`${values.sponsorType} agreement is required`);
      void form.trigger('sponsorId');
      return;
    }
    // 2. An EXPIRED insurance being the selected sponsor, which the schema
    //    can't see. Verbatim Flutter string.
    if (values.sponsorType === 'Insurance') {
      const list = (values.healthInsurances ?? []) as HealthInsurance[];
      const selected = list.find(
        (ins) =>
          !!ins.insuranceProvider?.id &&
          ins.insuranceProvider.id === values.sponsorId &&
          (ins.policyNumber ?? '') === (values.policyNo ?? ''),
      );
      const exp = selected?.expirationDate
        ? Date.parse(selected.expirationDate)
        : Number.NaN;
      if (Number.isFinite(exp) && exp < Date.now()) {
        toast.error('Selected Insurance is expired');
        return;
      }
    }

    // Reason-for-visit guard at the MUTATION boundary, not just on the button.
    //
    // This is the defect that produced the client's un-editable appointments.
    // Between 2026-04-07 (`9316b468`, first shipped create panel) and
    // 2026-05-07 (`f82fdd0d`, which added the `Reason for visit is required`
    // rule) the schema did NOT require `reasonCode`, the Save button gated on
    // `formState.isValid`, and `transform-form-to-request` shipped
    // `{ code: '', display: '', definition: '' }` unconditionally. Result:
    // appointments booked with an EMPTY reasonCode, empty from creation.
    //
    // Why that is unrecoverable rather than merely untidy: the API accepts the
    // empty-string object (200, persists as an empty string) but returns 500
    // for a JSON-null `reasonCode` — measured on dev 2026-09-09. So the empty
    // value sticks, and the schema's own required-rule then blocks every
    // subsequent save of that appointment.
    //
    // The button gate alone is one `isValid` refactor away from regressing, and
    // the transform's empty-string fallback is silent when it does. This guard
    // sits where the request is actually issued, in the same place as the
    // sponsor save-guard above, and fails loudly instead.
    if (!values.reasonCode?.code) {
      toast.error('Please select a reason for visit before saving.');
      return;
    }

    // EDIT mode — Flutter `UpdateAppointmentEvent(selectedAppointment)`.
    // Overlay the edited fields onto the loaded payload and PUT (NOT the
    // Book shape). See `form-edit-overlay.ts` + CONVERSION_NOTES.md.
    if (isEditMode && editAppointment) {
      const body = buildEditAppointmentBody(values, editAppointment, userId);
      updateAppointment.mutate(
        { appointmentId: editAppointment.id, body },
        {
          onSuccess: () => {
            toast.success('Appointment updated.');
            // Move the calendar onto the saved day BEFORE closing, so a
            // reschedule to another date doesn't drop the appointment out of
            // view. No-op when the day didn't change (the frame compares).
            onEditSaveSuccess?.(values.slotStart ?? null);
            // Inline rail closes on save; the full-page screen stays open
            // (closeOnSaveSuccess=false) so the user keeps their tab context.
            if (closeOnSaveSuccess) onClose();
          },
          // Failures surface via the global MutationCache.onError toast.
        },
      );
      return;
    }

    // The transform now derives speciality + invoice from the form
    // itself; the previous hardcoded `specialty: "general"` was the root
    // cause of the cascading gRPC failure.
    const request = transformFormToRequest(values, {
      organizationId,
      practiceId,
      roomId,
      createdBy: userId,
    });

    // Success: just close — global QueryClient.MutationCache.onError
    // handles failure via showServerError (per main's shared infra
    // rules), so no local logging is needed and no PHI ever lands in
    // the console.
    bookAppointment.mutate(request, {
      onSuccess: (created) => {
        // Success toast — matches the convention the rest of the
        // scheduling mutations use (BlockSlot, UnblockSlot, FU
        // Cancel/Send Reminder). Without it, the panel closes
        // silently and the user has no positive confirmation that
        // the appointment actually saved.
        toast.success('Appointment booked.');
        // FU Requests refetch — when this Book carried `followUpId`
        // (PROD-920 Create Follow Up flow), the backend marks the
        // originating FU request as completed. Invalidating the
        // `['follow-up-requests']` prefix forces the FU tab's list
        // query to refetch, so the row's status flips from
        // Requested → Completed (or drops out of the active view
        // entirely). Safe no-op for non-FU saves — the FU list
        // either isn't mounted, or refetching it is cheap.
        void qc.invalidateQueries({ queryKey: ['follow-up-requests'] });
        onSaveSuccess?.(buildSavedBookingSeed(created, values));
        if (closeOnSaveSuccess) onClose();
      },
      onError: (err) => {
        // SlotPassed recovery — Flutter `calendar_screen.dart:2355-2400`.
        // Server returns 400 with `{ errors: { SlotPassed: [...] } }`
        // when the slot the user picked has already started by the
        // time Book fires (common at the boundary of a 5-min slot,
        // or when the user opened the panel a while ago).
        //
        // Flutter clears slot.start / slot.end and re-fires PreBook
        // to refresh the slot picker. React mirrors that:
        //   1. Clear slotId / slotStart / slotEnd in form state.
        //   2. Invalidate the cached AvailableSlots query so the
        //      time-slots-section refetches with the latest data.
        //      (Section's pre-book hydration won't re-fire because
        //      hydratedRef already tracks this profile.)
        //
        // The global MutationCache onError still shows the toast
        // with the backend message; we don't suppress it.
        // SlotBooked joins SlotPassed (audit #8): the backend's book-time
        // guard (PROD-1844 TryClaimAsync) rejects a slot claimed by a
        // concurrent booking with the same shape of 400. Both mean "the
        // slot you see is no longer real" — same recovery: clear it,
        // refetch, and (AI lane) surface the rail's ConflictCard instead
        // of leaving Time checked while Save fails identically forever.
        if (
          err instanceof ApiError &&
          err.status === 400 &&
          typeof err.body === 'string' &&
          (err.body.includes('SlotPassed') || err.body.includes('SlotBooked'))
        ) {
          form.setValue('slotId', null);
          form.setValue('slotStart', null);
          form.setValue('slotEnd', null);
          // Narrowed invalidation (Samer review §5 on PR #217):
          // `useAvailableSlots` keys with practitionerId + practiceId
          // + appointmentProfileId in the params. Broad invalidation
          // (just `['available-slots']`) refetches every provider's
          // slot list on the strip — wasteful + visible flicker.
          // The form just lost its slot, so this provider/profile is
          // the only one that needs fresh data.
          const failedProviderId = form.getValues('providerId');
          const failedProfileId = form.getValues('appointmentProfileId');
          void qc.invalidateQueries({
            predicate: (query) => {
              if (query.queryKey[0] !== 'available-slots') return false;
              const params = query.queryKey[1] as
                | { practitionerId?: string; appointmentProfileId?: string }
                | undefined;
              if (!params) return false;
              return (
                params.practitionerId === failedProviderId &&
                params.appointmentProfileId === failedProfileId
              );
            },
          });
          // No toast here — the global MutationCache.onError handler
          // already surfaces the backend error message via
          // showServerError. Adding one here would double-fire.
          //
          // AI lane (audit #8): the rail must react too — without this the
          // ConflictCard plumbing exists but can never fire at the exact
          // moment the race happens, and the rail keeps showing Time as
          // done over a Save that will fail identically on every press.
          onAiSlotConflict?.();
        }
      },
    });
  };

  if (view === 'provider-selection') {
    return (
      <ProviderSelectionView
        providerFilter={providerFilter}
        onCancel={() => setView('create')}
        onOpenTimeDate={() => setView('time-date-selection')}
      />
    );
  }
  if (view === 'time-date-selection') {
    return <TimeDateSelectionView onCancel={() => setView('create')} />;
  }
  if (view === 'flags-selection') {
    return <FlagsPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'forms-selection') {
    return <FormsPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'reminder-selection') {
    return <ReminderPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'reminder-editor') {
    return <ReminderEditorView onClose={() => setView('create')} />;
  }
  if (view === 'recurrence-config') {
    return <RecurrencePickerView onClose={() => setView('create')} />;
  }
  if (view === 'create-contact') {
    return <CreateContactView onClose={() => setView('create')} />;
  }
  if (view === 'edit-insurance') {
    return (
      <EditInsuranceView
        onClose={() => setView('create')}
        organizationId={organizationId}
        practiceId={practiceId}
      />
    );
  }
  if (view === 'create-patient') {
    return (
      <CreatePatientView
        onClose={() => setView('create')}
        organizationId={organizationId}
        practiceId={practiceId}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={cn(
        'flex items-center justify-between border-b border-border-default flex-shrink-0',
        modal ? 'px-6 py-4' : 'px-4 py-3',
      )}>
        <h2 className={cn(
          'font-semibold text-text-primary',
          modal
            ? 'text-[length:var(--type-title-size)] leading-[var(--type-title-line)]'
            : 'text-[length:var(--type-section-size)] leading-[var(--type-section-line)]',
        )}>
          {mode === 'create'
            ? 'New appointment'
            : mode === 'edit'
              ? 'Edit appointment'
              : mode === 'reschedule'
                ? 'Reschedule'
                : 'Cancel appointment'}
        </h2>
        <div className="flex items-center gap-2">
          {/* Open in full view / Back to side panel (Antonin, 15 Oct): a full secondary button with its label ("a full button
              with text"), the same form, every value kept. */}
          {onToggleExpanded ? (
            <Button type="button" variant="secondary" size="sm" onClick={onToggleExpanded} data-booking-expand>
              {expanded ? <Minimize2 data-icon="inline-start" aria-hidden /> : <Maximize2 data-icon="inline-start" aria-hidden />}
              {expanded ? 'Back to side panel' : 'Open in full view'}
            </Button>
          ) : null}
          {/* In the full view, × goes back to the rail (nothing lost); only the footer's Cancel ends the booking. */}
          <button
            type="button"
            onClick={expanded && onToggleExpanded ? onToggleExpanded : onClose}
            aria-label={expanded ? 'Close full view' : 'Close'}
            className={HEADER_ICON_BUTTON}
          >
            <X size={modal ? 20 : 18} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Scrollable body — plain div with `scrollbar-hide` so the bar
          stays off while scroll still works. */}
      <div data-booking-body className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="pb-2">
          {/* APPOINTMENT STATUS — edit mode only, for every status (Flutter
              `create_appointment_widget.dart:2537` `isEdit ?
              AppointmentStatusWidget(...) : Container()`). Hidden in reschedule
              (Flutter suppresses the status widget when isReschedule). */}
          {isEditOnly ? (
            <AppointmentStatusSummary
              currentStatus={editAppointment?.status}
              referenceId={editAppointment?.referenceId}
              cancelationReason={editAppointment?.cancelationReason}
              cancellationNote={editAppointment?.cancellationNote}
            />
          ) : null}

          {/* 0 — Completion rail (Scheduling-AI drafts only): the generated
              checklist over the locked card vocabulary. One open ask at a
              time; answered cards collapse to checked rows; taps route to
              the same views/sections the manual flow uses. */}
          {aiDraft ? (
            <CompletionRail
              draft={aiDraft}
              conflict={aiConflict}
              onAction={handleCompletionAction}
              onDismiss={onAiDismiss}
              // Hoisted above the subview early-returns so it survives a
              // picker round-trip (Codex P1(a)). Omitted → the rail falls
              // back to its own local instance and behaves exactly as
              // before, so this stays fail-closed for other callers.
              choreography={aiRailChoreography}
            />
          ) : null}

          {/* 0b — Legacy AI-fill chip strip: no-draft fallback only (kept
              for surfaces that pass a report without the draft object). */}
          {!aiDraft && aiFillReport && aiFillReport.length > 0 ? (
            <div
              className="px-4 py-3 border-t border-border-subtle"
              data-testid="ai-fill-report"
            >
              <p className="text-xs font-medium text-text-muted mb-2">
                From your request
              </p>
              <div className="flex flex-wrap gap-2">
                {aiFillReport.map((item) =>
                  item.state === 'filled' ? (
                    <span
                      key={item.key}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-blue-100 text-brand-primary"
                    >
                      <span aria-hidden>✓</span> {item.label}
                    </span>
                  ) : (
                    <button
                      key={item.key}
                      type="button"
                      disabled={!item.view}
                      onClick={() => item.view && setView(item.view)}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-status-warning-bg text-status-warning-text border border-status-warning/40 disabled:cursor-default"
                    >
                      <span aria-hidden>!</span> {item.label}
                    </button>
                  ),
                )}
              </div>
            </div>
          ) : null}

          {/* 1 — Provider (first: the front desk picks the doctor or finds
              one by specialty). ALWAYS locked in edit mode. */}
          <div data-completion-target="provider">
            <ProviderSection
              organizationId={organizationId}
              practiceId={practiceId}
              onOpenSearch={() => setView('provider-selection')}
              locked={isEditMode}
              providerFilter={providerFilter}
            />
          </div>

          {/* 2 — Location (after provider: each provider may offer different
              location types). Shown once a provider is picked. */}
          {providerId ? <div data-completion-target="location"><LocationSelector /></div> : null}

          {/* 3 — Appointment Profile (after provider + location). */}
          {providerId ? (
            <div data-completion-target="visit-type">
              <AppointmentProfileSection
                organizationId={organizationId}
                practiceId={practiceId}
                keepLoadedReminder={isEditMode && !isRescheduleMode}
                constrainToAppointmentTypeCode={
                  isRescheduleMode
                    ? (editAppointment?.appointmentType?.code ?? null)
                    : null
                }
                profileEditLocked={profileEditLocked}
                reasonLocked={reasonLocked}
                billingLocked={billingLocked}
              />
            </div>
          ) : null}

          {/* Status-gated group A — read-only when the appointment's status
              is past Booked/Proposed/Pending (Flutter `showEdit`). */}
          <SectionLock locked={sectionsLocked}>
            {/* 4 — Date & time slots (after profile: availability depends on
                provider + location + profile). */}
            {providerId && appointmentProfileId ? (
              <div data-completion-target="slot">
                <DatePickerSection />
                <TimeSlotsSection
                  practiceId={practiceId}
                  onOpenTimeDate={() => setView('time-date-selection')}
                />
              </div>
            ) : null}

            {/* 5 — Patient. Locked in EVERY edit mode. */}
            <div data-completion-target="patient">
              <PatientSection
                onCreatePatient={() => setView('create-patient')}
                locked={isEditMode}
                initialSearchText={initialPatientQuery}
              />
            </div>

            {/* 7 — Previous Appointment (FOLLOWUP only, Flutter slot 4) */}
            <div data-completion-target="previous-appointment"><PreviousAppointmentSection /></div>

            {/* 8 — Residential Address (Home location only, Flutter slot 5) */}
            <div data-completion-target="address"><ResidentialAddressSection /></div>

            {/* 9 — Callback Contact (after patient picked, Flutter slot 6) */}
            {patientId ? (
              <CallbackContactSection onCreateContact={() => setView('create-contact')} />
            ) : null}

            {/* 10 — Reminder (auto-populated by PreBookSelectedAppointmentProfile;
                pencil opens manual-override picker). Locked in RESCHEDULE mode —
                Flutter disables reminder editing while rescheduling
                (`create_appointment_widget.dart` reminder `showEdit:
                isReschedule == false`). */}
            <SectionLock locked={isRescheduleMode}>
              <div data-completion-target="reminder">
                <ReminderSection onEdit={() => setView('reminder-editor')} />
              </div>
            </SectionLock>
          </SectionLock>

          {/* 11 — Payment (after patient). NOT gated by appointment status, but
              Flutter DOES gate it on the invoice (`payment_widget.dart:359`):
              the whole sponsor block is locked once the invoice leaves Draft
              or the appointment is cancelled. The IgnorePointer there wraps the
              entire body (incl. the Edit-insurance link), so we wrap the whole
              section here too — same as the View popup (PROD-1010). */}
          {patientId ? (
            <SectionLock locked={paymentLocked}>
              <div data-completion-target="receipt" />
              <PaymentSection
                organizationId={organizationId}
                practiceId={practiceId}
                onEditInsurance={() => setView('edit-insurance')}
                onAddInsurance={() => setView('edit-insurance')}
                // Edit mode: the persisted invoice drives the TOTAL (Flutter
                // `payment_widget.dart:307`). `editAppointment` is the live
                // PreLoadAppointmentDetails appointment, refetched on every
                // invoice edit, so the card stays in sync (PROD-1809).
                editInvoice={editAppointment?.invoice ?? null}
              />
            </SectionLock>
          ) : null}

          {/* Status-gated group B — Note / Flags / Send Form / Recurrence +
              the bottom action row that seeds them (Flutter slot row). */}
          <SectionLock locked={sectionsLocked}>
            <NoteSlot />
            <FlagsSection onEdit={() => setView('flags-selection')} />
            <SendFormRow onEdit={() => setView('forms-selection')} />
            <RecurrenceSlot onEdit={() => setView('recurrence-config')} />

            {/* Bottom action row — only when provider is picked */}
            {providerId ? (
              <ActionsRow
                onOpenFlags={() => setView('flags-selection')}
                onOpenSendForm={() => setView('forms-selection')}
                onOpenReminder={() => setView('reminder-selection')}
                onOpenRecurrence={() => setView('recurrence-config')}
                reminderAddDisabled={reminderAddLocked}
                hideRecurrence={isRescheduleMode}
              />
            ) : null}
          </SectionLock>

          {/* Appointment status menu — left-panel vertical action set
              (Flutter create_appointment_widget.dart:2562-2707). Transitions +
              Cancel, gated by the 3-day status-button window, per current
              status. Edit-only; hidden in reschedule (isEditOnly === false).
              Deliberately OUTSIDE SectionLock so it stays available for
              Arrived / CheckedIn where the form sections are read-only.
              AppointmentStatusManager owns its Cancel + arrived-guard dialogs
              and refreshes appointment-details after each mutation. */}
          {isEditOnly && editAppointment ? (
            <div className="pt-2">
              <AppointmentStatusManager
                appt={editAppointment}
                variant="panel"
                onAfterMutation={() => {
                  qc.invalidateQueries({ queryKey: ['appointment-details'] });
                }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <PanelFooter
        mode={mode}
        isSubmitting={isSubmitting}
        modal={modal}
        // RHF's `formState.isValid` lags behind `errors` in some flows
        // (mode: 'onChange' + zodResolver doesn't always flip isValid
        // back to true after the last error clears). Deriving validity
        // from the errors object directly is reliable.
        //
        // Rule 5 on the REAL control (audit #9): with an AI draft active
        // the engine's gate joins Zod — an active ConflictCard or an
        // unconfirmed recurrence-dates card blocks Book even when the
        // form itself validates; the rail's open card is the explanation.
        isValid={Object.keys(form.formState.errors).length === 0 && aiBookGate}
        missingFields={collectErrorPaths(form.formState.errors)}
        rawErrors={form.formState.errors}
        onSave={handleSave}
        onCancel={onClose}
      />
    </div>
  );
}

export function CreateAppointmentPanel({
  open,
  onClose,
  initialValues,
  edge = 'right',
  layout = 'wide',
  modal = false,
  ...props
}: CreateAppointmentPanelProps) {
  // "Open in full view" (Antonin, 15 Oct: "a secondary button / icon to open it in a central modal (opening it keeps all
  // parameters)"). ONE form instance: it renders into a stable host node, and the host moves between the rail and the
  // Dialog. React never remounts it (the portal's container never changes), so values, the open sub-view, searches
  // and fetched slots all survive the trip both ways. A step toward the full-screen booking (Notion "Booking 3/5").
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.className = 'flex h-full min-h-0 flex-col';
    return el;
  });
  const railSlotRef = useRef<HTMLDivElement | null>(null);
  // A moved node forgets its scroll: read it while it is still on screen, give it back once it lands.
  const scrollTopRef = useRef(0);
  const body = useCallback(() => host.querySelector<HTMLElement>('[data-booking-body]'), [host]);
  const moveHost = useCallback((to: HTMLElement) => {
    if (host.parentNode === to) return;
    to.appendChild(host);
    const el = body();
    if (el) el.scrollTop = scrollTopRef.current;
  }, [host, body]);
  const setExpandedKeepingScroll = useCallback((next: boolean) => {
    scrollTopRef.current = body()?.scrollTop ?? 0;
    setExpanded(next);
  }, [body]);
  // Callback refs place the host during the commit, before the form's own layout effects run, so the form is always
  // measured, focused and scrolled inside the page, never detached.
  const railSlot = useCallback((el: HTMLDivElement | null) => {
    railSlotRef.current = el;
    if (el && !expandedRef.current) moveHost(el);
  }, [moveHost]);
  const modalSlot = useCallback((el: HTMLDivElement | null) => {
    if (el) moveHost(el);
  }, [moveHost]);
  useLayoutEffect(() => {
    const rail = railSlotRef.current;
    if (!expanded && rail) moveHost(rail);
  }, [expanded, moveHost]);
  // Ending the booking (Cancel, a save that closes) leaves nothing expanded for the next one.
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  if (!open) return null;

  // When the user clicks ANOTHER slot or profile chip while the panel
  // is already open, `pendingInitialValues` in the parent updates, but
  // `useForm({ defaultValues })` inside `AppointmentFormProvider` only
  // initializes on mount — subsequent prop changes are ignored. Re-key
  // the provider on the identity of the picked context so a new click
  // remounts the provider with the new defaults. Composite of the
  // fields that change per click (provider, slot, profile, date); when
  // the panel opens fresh via the toolbar +Create (no initialValues),
  // the key resolves to a stable empty composite, so multiple opens
  // don't unnecessarily remount.
  const formKey = [
    initialValues?.providerId ?? '',
    initialValues?.appointmentProfileId ?? '',
    initialValues?.slotStart ?? '',
    initialValues?.selectedDate ?? '',
    initialValues?.referenceAppointmentId ?? '',
    // Scheduling-AI patient-search seed — a new AI draft with the same
    // date but a different patient must still remount the form so the
    // Patient section picks up the new initial search text.
    props.initialPatientQuery ?? '',
    // AI draft identity (Codex review on #836): drafts never set slotStart, so
    // two commands for the same patient/provider/day (e.g. recurring → one-off)
    // hashed identically and the old recurrence payload + view survived.
    props.aiDraftKey ?? '',
  ].join('|');

  // Already inside a modal (CreateAppointmentModal): no rail to go back to, so no switch.
  if (modal) {
    return (
      <div data-edge={edge} className="bg-surface-card flex flex-col h-full overflow-hidden">
        <AppointmentFormProvider key={formKey} initialValues={initialValues}>
          <CreateAppointmentForm onClose={onClose} modal {...props} />
        </AppointmentFormProvider>
      </div>
    );
  }

  return (
    <>
      {/* The rail: fixed width + side border against the calendar. While the form is in the full view the rail steps
          aside (the calendar takes the room back, dimmed behind the modal) and slides back in on return. */}
      <div
        data-edge={edge}
        className={cn(
          'bg-surface-card flex flex-col h-full overflow-hidden flex-shrink-0 border-border-default',
          layout === 'wide' ? 'w-[clamp(424px,33vw,560px)]' : 'w-[384px]',
          edge === 'right' ? 'border-l' : 'border-r',
          expanded && 'hidden',
        )}
      >
        <div ref={railSlot} className="flex h-full min-h-0 flex-col" />
      </div>

      {createPortal(
        <AppointmentFormProvider key={formKey} initialValues={initialValues}>
          <CreateAppointmentForm
            onClose={onClose}
            modal={expanded}
            expanded={expanded}
            onToggleExpanded={() => setExpandedKeepingScroll(!expanded)}
            {...props}
          />
        </AppointmentFormProvider>,
        host,
      )}

      {/* The full view: the app's Dialog, large (800, --dialog-width-large), 90 vh, its own scroll. ×, Esc and the backdrop all go back
          to the rail; only the form's Cancel ends the booking. */}
      <Dialog open={expanded} onOpenChange={(next) => { if (!next) setExpandedKeepingScroll(false); }} modal>
        <DialogPortal>
          <DialogOverlay className="bg-black/40 supports-backdrop-filter:backdrop-blur-[6px]" />
          <DialogPrimitive.Popup
            data-slot="dialog-content"
            data-booking-full-view
            className={cn(
              'fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[calc(100vw-64px)] max-w-[var(--dialog-width-large)] h-[90vh]',
              'overflow-hidden rounded-[var(--radius-lg)] bg-surface-card shadow-2xl outline-none',
              'duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            )}
          >
            <DialogTitle className="sr-only">
              {props.mode === 'edit' ? 'Edit appointment' : props.mode === 'reschedule' ? 'Reschedule' : 'New appointment'}
            </DialogTitle>
            <div ref={modalSlot} className="flex h-full min-h-0 flex-col" />
          </DialogPrimitive.Popup>
        </DialogPortal>
      </Dialog>
    </>
  );
}
